export const CHECKOUT_URL = 'https://veneer-en.impultienda.ar/checkout/'

export const HERO_MOCKUP =
  'https://cdn.magicpatterns.com/uploads/7iLXsHmcnmBs66xpBVPVjm/MOCKUP_PRINCIPAL_VENEER_sin_fondo.png'

export const AUTHOR_PHOTO =
  'https://cdn.magicpatterns.com/uploads/sSB1A9NB7XrEGEvwSRrWEZ/Dra._Karito_Odontologa_OF.png'

export const stats = [
  { value: '+40', label: 'Visual tools & checklists' },
  { value: '5', label: 'Key variables for natural-looking veneers' },
  { value: '<15 MIN', label: 'To analyze & plan a case' },
  { value: '100%', label: 'Visual step-by-step system' },
]

export const beforeItems = [
  'You weren’t sure what needed to be corrected.',
  'You chose shades and layers without a clear framework.',
  'You saw amazing veneer cases on Instagram but couldn’t understand what made them look so natural.',
  'You kept adding or removing composite trying to fix the result.',
  'You struggled to identify why a veneer looked artificial.',
  'Every new case felt like starting from scratch.',
]

export const afterItems = [
  'Analyze each case using a clear visual sequence.',
  'Evaluate the variables that influence natural appearance.',
  'Understand the visual role of each layer.',
  'Use visual maps to analyze color and translucency.',
  'Follow checklists before, during, and after the procedure.',
  'Use the same analysis framework across different cases.',
]

export const painPoints = [
  {
    emoji: '😰',
    title: 'Your veneers look too artificial',
    text: 'Technically correct — but they still don’t have the natural appearance you were aiming for.',
  },
  {
    emoji: '🤷',
    title: 'You’re unsure which composite, shade, or layer to use',
    text: 'You have multiple options in front of you, but you’re not always sure which one fits the final result you’re trying to achieve.',
  },
  {
    emoji: '👀',
    title: 'You see incredible veneer cases online but don’t understand what you’re doing differently',
    text: 'You know your results could look more natural, but you can’t pinpoint exactly where the difference comes from.',
  },
  {
    emoji: '😓',
    title: 'You struggle to identify the problem while you’re building the veneer',
    text: 'When the restoration starts looking different from what you expected, you’re not always sure which variable to check first.',
  },
  {
    emoji: '💸',
    title: 'You want to take on more valuable veneer cases — but you don’t fully trust your process yet',
    text: 'You don’t want to rely on improvisation when you’re working chairside.',
  },
  {
    emoji: '😩',
    title: 'Every veneer case feels like a new experiment',
    text: 'You keep changing materials, shapes, or layering techniques hoping you’ll finally get the result you’re looking for.',
  },
]

export type Blueprint = {
  roman: string
  title: string
  text: string
  bullets?: string[]
}

export const blueprints: Blueprint[] = [
  {
    roman: 'I',
    title: 'The Natural Veneer Map',
    text: 'Discover the 5 key variables you need to analyze to understand why a veneer looks natural… or artificial.',
    bullets: ['Anatomy', 'Color', 'Layering', 'Texture', 'Finishing'],
  },
  {
    roman: 'II',
    title: 'Natural Anatomy Blueprint',
    text: 'Learn how to analyze shape, volume, contours, transitions, and texture — the visual elements that influence how natural an anterior restoration appears. Stop thinking only about making a “beautiful tooth.” Start analyzing what makes it look like a real tooth.',
  },
  {
    roman: 'III',
    title: 'Color & Translucency Blueprint',
    text: 'Learn how to analyze value, chroma, hue, and translucency to make more structured decisions during shade selection and layering.',
  },
  {
    roman: 'IV',
    title: 'Layering Logic™',
    text: 'Understand the visual role of each layer and the logic behind the sequence of composite stratification.',
    bullets: ['Dentin', 'Enamel', 'Translucency', 'Internal effects', 'Final layer'],
  },
  {
    roman: 'V',
    title: 'Sculpting Blueprint',
    text: 'A visual reference for analyzing shape, contour, line angles, incisal anatomy, surface texture and micro-anatomy — so you can evaluate the restoration before reaching the final finishing stage.',
    bullets: ['Shape', 'Contour', 'Line angles', 'Incisal anatomy', 'Surface texture', 'Micro-anatomy'],
  },
  {
    roman: 'VI',
    title: 'Finishing & Polishing Blueprint',
    text: 'Discover what to evaluate during finishing and polishing so a well-built restoration doesn’t lose its natural appearance during the final stages.',
  },
  {
    roman: 'VII',
    title: 'Veneer Troubleshooting System',
    text: 'The simple framework: “IF YOU SEE THIS → CHECK THIS.” Learn how to identify possible causes when a veneer presents:',
    bullets: [
      'Excessive opacity',
      'Excessive volume',
      'Lack of depth',
      'Flat appearance',
      'Incorrect shade',
      'Artificial anatomy',
      'Insufficient texture',
      'Poor finishing',
    ],
  },
  {
    roman: 'VIII',
    title: 'Case Analysis Blueprint',
    text: 'Learn how to break down an entire veneer case — from the desired final result to the key decisions you should consider before starting.',
  },
]

export const tools = [
  {
    emoji: '🗺️',
    title: 'Natural Veneer Decision Map',
    text: 'A visual decision map that helps you move through the key questions of a veneer case in the right order.',
  },
  {
    emoji: '🎨',
    title: 'Color Selection Matrix',
    text: 'A visual reference for organizing your shade and translucency decisions.',
  },
  {
    emoji: '🦷',
    title: 'Anatomy Evaluation Sheet',
    text: 'A practical worksheet for evaluating shape, volume, texture, and contours.',
  },
  {
    emoji: '📋',
    title: 'Veneer Master Checklist',
    text: 'A checklist to review the process before, during, and after veneer construction.',
  },
  {
    emoji: '🔍',
    title: 'Final Result Scorecard',
    text: 'A visual evaluation tool to review the finished restoration using structured criteria.',
  },
  {
    emoji: '⚠️',
    title: '30 Veneer Mistakes Checklist',
    text: 'A quick-reference list of common mistakes that can affect the natural appearance of resin veneers.',
  },
]

export type Bonus = {
  number: string
  title: string
  value: string
  text: string
  image: string
}

export const bonuses: Bonus[] = [
  {
    number: 'BONUS #1',
    title: 'Natural Anatomy Atlas',
    value: '$47',
    text: 'A visual reference library covering the key anatomical characteristics that contribute to a more natural-looking anterior restoration.',
    image: 'https://cdn.magicpatterns.com/uploads/bn6iGgXwaF8wY7JpjQirgL/BONUS_1_VENEER-EN.png',
  },
  {
    number: 'BONUS #2',
    title: 'Resin Selection Matrix™',
    value: '$37',
    text: 'A practical matrix for organizing material selection and connecting different material characteristics with the visual effect you’re trying to achieve.',
    image: 'https://cdn.magicpatterns.com/uploads/6EbKAn1MqNtkjojPqdxPgs/BONUS_2_VENEER-EN.png',
  },
  {
    number: 'BONUS #3',
    title: 'Color Mistake Detector™',
    value: '$37',
    text: 'A visual troubleshooting guide: “IF IT LOOKS LIKE THIS → CHECK THESE VARIABLES.” Designed to help you identify potential problems with value, chroma, hue, and translucency.',
    image: 'https://cdn.magicpatterns.com/uploads/n4bp6iwm6w52uyJ369r9m6/BONUS_3_VENEER-EN.png',
  },
  {
    number: 'BONUS #4',
    title: '30 Veneer Mistakes',
    value: '$29',
    text: 'Common mistakes organized by stage: Diagnosis → Color → Layering → Anatomy → Finishing → Polishing.',
    image: 'https://cdn.magicpatterns.com/uploads/2ArbR5HWLGktAwMv8785Hy/BONUS_4_VENEER-EN.png',
  },
  {
    number: 'BONUS #5',
    title: 'Chairside Quick Reference',
    value: '$27',
    text: 'A compact version of the most important maps and checklists so you can quickly reference them whenever you need them.',
    image: 'https://cdn.magicpatterns.com/uploads/nMM9zL3QQyWFhx4UqHUjxu/BONUS_5_VENEER-EN.png',
  },
]

export const testimonials = [
  {
    name: 'Dr. Carlos M.',
    country: '🇲🇽 Mexico',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/2d89f423-2400-4e22-863e-0e61d34d7413.jpg',
    quote:
      'I used to look at a case and not understand exactly what I was doing differently. The Blueprint helped me break the problem down into variables that are much easier to analyze.',
  },
  {
    name: 'Dr. Mariana C.',
    country: '🇦🇷 Argentina',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/fd82d347-aaf2-41e6-a8a4-e6b9cb0e9f43.jpg',
    quote:
      'What I liked most is that it’s not another endless course. It’s very visual, and I can go straight back to the section I need when I’m preparing a case.',
  },
  {
    name: 'Dr. Roberto S.',
    country: '🇨🇴 Colombia',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/9a4cf2d4-b2a7-4adf-be4a-8ff3cb93f775.jpg',
    quote:
      'The color and layering sections helped me understand why some of my restorations looked artificial even when the technique itself was correct.',
  },
  {
    name: 'Dr. Fernanda L.',
    country: '🇵🇪 Peru',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/8a46e63f-bdde-479d-8188-c658330729ab.jpg',
    quote:
      'Now I have a sequence for analyzing my cases. Before, I would simply start and hope the final result looked right.',
  },
  {
    name: 'Dr. André R.',
    country: '🇨🇱 Chile',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/3b7ac77e-9873-4f71-8ec5-06d19eca3016.jpg',
    quote:
      'It didn’t feel like I was buying another collection of PDFs. It felt like I was buying a reference tool for my veneer cases.',
  },
]

export const faqs = [
  {
    q: 'How will I receive the material?',
    a: 'Immediately after your purchase, you’ll receive access to download The Natural Veneer Blueprint™ and all included bonuses.',
  },
  {
    q: 'Do I need to be a specialist?',
    a: 'The material is designed primarily for dentists who perform or want to perform resin veneers and want to structure their analysis, planning, and decision-making process more effectively.',
  },
  {
    q: 'Does it work in every country?',
    a: 'Yes. The system focuses on principles of analysis, planning, anatomy, color, layering, finishing, and evaluation. Material selection should always take into account the products available in your market and your professional clinical judgment.',
  },
  {
    q: 'Are the files downloadable?',
    a: 'Yes. You’ll receive digital materials that you can access from your computer, tablet, or phone, with printable versions of the key worksheets and checklists.',
  },
  {
    q: 'Is this a one-time payment?',
    a: 'Yes. You pay once — $27 USD — and receive lifetime access to the purchased materials.',
  },
  {
    q: 'Does this replace hands-on clinical training?',
    a: 'No. The Natural Veneer Blueprint™ is an educational and reference tool. It does not replace hands-on clinical education, professional supervision, manufacturer instructions, or your own clinical judgment.',
  },
  {
    q: 'What if I don’t like it?',
    a: 'You’re protected by our 7-day money-back guarantee. Review the material and, if you don’t feel it provides value to your practice, you can request a refund within the guarantee period.',
  },
]

export const purchaseNotifications = [
  { name: 'Dr. Valentina R.', place: 'Bogotá, Colombia' },
  { name: 'Dr. Javier M.', place: 'Guadalajara, Mexico' },
  { name: 'Dr. Camila S.', place: 'Santiago, Chile' },
  { name: 'Dr. Andrés P.', place: 'Lima, Peru' },
  { name: 'Dr. Lucía F.', place: 'Buenos Aires, Argentina' },
  { name: 'Dr. Mateo G.', place: 'Quito, Ecuador' },
  { name: 'Dr. Daniela T.', place: 'Montevideo, Uruguay' },
  { name: 'Dr. Sofía L.', place: 'Madrid, Spain' },
  { name: 'Dr. Ricardo A.', place: 'San José, Costa Rica' },
  { name: 'Dr. Paula N.', place: 'Asunción, Paraguay' },
  { name: 'Dr. Emilio C.', place: 'Panama City, Panama' },
  { name: 'Dr. Gabriela V.', place: 'Santo Domingo, Dominican Republic' },
]
